import { Link } from 'react-router-dom';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-4 leading-snug">
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg md:text-xl font-bold text-gray-800 mt-8 mb-3">
      {children}
    </h3>
  );
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-gray-600 text-base leading-relaxed mb-4">
      {children}
    </p>
  );
}

function TipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-teal-50 border-l-4 border-teal-400 rounded-r-xl px-6 py-5 my-6">
      <div className="flex items-start gap-3">
        <i className="ri-lightbulb-line text-teal-500 text-xl mt-0.5 shrink-0"></i>
        <p className="text-teal-800 text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

interface TentCardProps {
  number: string;
  name: string;
  size: string;
  icon: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

function TentCard({ number, name, size, icon, description, imageUrl, imageAlt }: TentCardProps) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
      <div className="w-full h-56 md:h-64 bg-gray-100 overflow-hidden">
        <img
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="p-6">
        <div className="flex items-start gap-4 mb-3">
          <div className="shrink-0 flex flex-col items-center gap-1">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl">
              <i className={`${icon} text-teal-500 text-lg`}></i>
            </div>
            <span className="text-xs font-bold text-teal-400">{number}</span>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 text-base leading-snug">{name}</h4>
            <span className="inline-block mt-1 px-2.5 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full font-medium">{size}</span>
          </div>
        </div>
        <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

const tents: TentCardProps[] = [
  {
    number: '01',
    name: 'Yoga Sol',
    size: '50ft Diameter Hexagon',
    icon: 'ri-mental-health-line',
    description: 'A tranquil oasis for large group yoga sessions, meditation workshops, and wellness talks. The organic shape and natural colours blended perfectly with the surrounding environment, creating a serene atmosphere that encouraged relaxation and mindfulness.',
    imageUrl: '/assets/img/f56e1c662297.webp',
    imageAlt: 'Yoga Sol stretch tent at Lightning in a Bottle festival',
  },
  {
    number: '02',
    name: 'Family Campsite',
    size: "20' x 30'",
    icon: 'ri-home-heart-line',
    description: 'A safe, comfortable, and shaded area for families attending the festival. The tent offered protection from the elements while maintaining an open and airy feel, ensuring families could enjoy the festival together without feeling cramped.',
    imageUrl: '/assets/img/7b439e949df5.webp',
    imageAlt: 'Family campsite stretch tent at Lightning in a Bottle',
  },
  {
    number: '03',
    name: 'Artclave Art Gallery',
    size: "2 Joined 30' x 50' Tents",
    icon: 'ri-gallery-line',
    description: 'Two joined tents forming a spacious and flowing art gallery. The elegant curves and high ceilings created an inviting atmosphere that encouraged attendees to explore the art on display. The Artclave became a focal point of the festival.',
    imageUrl: '/assets/img/7a6c28365956.webp',
    imageAlt: 'Artclave art gallery stretch tents at Lightning in a Bottle',
  },
  {
    number: '04',
    name: 'Tipping Point Bar',
    size: "50' x 65'",
    icon: 'ri-goblet-line',
    description: 'A hub of social activity throughout the festival. The open design allowed for easy flow of people in and out, while the sturdy construction ensured it could withstand constant foot traffic. The tent\'s sleek appearance complemented the bar\'s vibrant atmosphere.',
    imageUrl: '/assets/img/a66718d1acd9.webp',
    imageAlt: 'Tipping Point Bar stretch tent at Lightning in a Bottle',
  },
  {
    number: '05',
    name: 'Atlaswyld Changing Rooms',
    size: "20' x 30'",
    icon: 'ri-shirt-line',
    description: 'A private and stylish space where glampers could freshen up and change in comfort. Designed with privacy and functionality in mind, featuring well-organised areas for changing, storing belongings, and relaxing.',
    imageUrl: '/assets/img/f08180d64760.webp',
    imageAlt: 'Atlaswyld glamping changing rooms stretch tent',
  },
  {
    number: '06',
    name: 'Kids Zone',
    size: "2 x 30' x 40' Tents",
    icon: 'ri-gamepad-line',
    description: 'A family-friendly area where children could play, learn, and create in a safe environment. The open design allowed for easy supervision by parents and staff, while the bright and cheerful colours added to the playful atmosphere.',
    imageUrl: '/assets/img/7534416be9bd.webp',
    imageAlt: 'Kids Zone stretch tents at Lightning in a Bottle festival',
  },
  {
    number: '07',
    name: 'Haven Treatment & Spa',
    size: "40' x 60'",
    icon: 'ri-heart-pulse-line',
    description: 'A sanctuary for relaxation and rejuvenation, offering a range of treatments and spa services. The soft, flowing lines and natural colours created a calming environment perfect for massages, facials, and other spa treatments.',
    imageUrl: '/assets/img/07f04ba0f12e.webp',
    imageAlt: 'Haven spa and treatment stretch tent at Lightning in a Bottle',
  },
  {
    number: '08',
    name: 'Artist Relations',
    size: "30' x 40'",
    icon: 'ri-user-star-line',
    description: 'A functional and comfortable space for performers and their teams. Located near the main stages, the tent allowed for convenient access while ensuring privacy. Artists could check in, meet with festival managers, and prepare for their performances.',
    imageUrl: '/assets/img/4b98e46f102a.webp',
    imageAlt: 'Artist Relations stretch tent at Lightning in a Bottle',
  },
  {
    number: '09',
    name: 'Atlaswyld Glamping Bar',
    size: "30' x 40'",
    icon: 'ri-cup-line',
    description: 'A central feature of the glamping area, offering a stylish and sophisticated space where glampers could enjoy a drink and socialise in comfort. The sleek design and high-quality materials added to the luxurious atmosphere of the glamping area.',
    imageUrl: '/assets/img/e6713d8f2248.webp',
    imageAlt: 'Atlaswyld glamping bar stretch tent at Lightning in a Bottle',
  },
];

const statsData = [
  { value: '17', label: 'Stretch Tents Deployed' },
  { value: '9', label: 'Unique Venue Spaces' },
  { value: '7', label: 'Days of Festival Coverage' },
  { value: '100%', label: 'Positive Organiser Feedback' },
];

export default function ArticleBody() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16">

      {/* Intro */}
      <Paragraph>
        At Pop Pro Tents, we take pride in transforming events into unforgettable experiences through our versatile and aesthetically pleasing stretch tents. We had the privilege of being a part of Lightning in a Bottle, a renowned music and arts festival that brings together a vibrant community of creatives, musicians, and free spirits.
      </Paragraph>
      <Paragraph>
        Our stretch tents played a crucial role in elevating the festival experience for attendees, providing functional and visually striking spaces that blended seamlessly with the festival's ethos of creativity, connection, and sustainability. This case study delves into how our tents were used across the festival, highlighting the versatility and impact of Pop Pro Tents.
      </Paragraph>

      {/* Hero image */}
      <div className="rounded-2xl overflow-hidden my-8 w-full h-72 md:h-96 bg-gray-100">
        <img
          src="/assets/img/ac940e4a13f7.webp"
          alt="Pop Pro Tents at Lightning in a Bottle Festival"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-10">
        {statsData.map((stat) => (
          <div key={stat.label} className="bg-gray-50 rounded-2xl p-5 text-center">
            <p className="text-3xl font-extrabold text-teal-500 mb-1">{stat.value}</p>
            <p className="text-gray-500 text-xs leading-snug">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Overview */}
      <SectionHeading>Overview of Lightning in a Bottle Festival</SectionHeading>
      <Paragraph>
        Lightning in a Bottle is not just a music festival; it's a celebration of art, culture, and sustainability. Held annually in California, this event draws thousands of attendees who come to immerse themselves in a unique blend of music, art installations, workshops, and wellness activities. The festival is known for its emphasis on community, environmental consciousness, and creating transformative experiences for everyone who attends.
      </Paragraph>
      <Paragraph>
        When the festival organisers approached us at Pop Pro Tents, they were looking for a partner who could provide structures that were not only functional but also aligned with the festival's aesthetic and environmental values. They needed versatile, durable, and visually appealing tents that could accommodate a variety of uses, from yoga sessions to art exhibits, to glamping amenities. We were excited to rise to the challenge and deliver a solution that would enhance the festival experience.
      </Paragraph>

      {/* Role section */}
      <SectionHeading>The Role of Pop Pro Tents at Lightning in a Bottle</SectionHeading>
      <Paragraph>
        For the Lightning in a Bottle Festival, Pop Pro Tents provided a total of <strong>17 stretch tents</strong>, each uniquely tailored to serve a specific purpose. Our tents were strategically placed across the festival grounds, creating dedicated spaces for different activities and enhancing the overall ambiance of the event.
      </Paragraph>

      <TipBox>
        Each tent was custom-configured for its specific use case, from the hexagonal yoga space to the joined art gallery tents, demonstrating the unmatched flexibility of stretch tent structures.
      </TipBox>

      {/* Tent cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        {tents.map((tent) => (
          <TentCard key={tent.number} {...tent} />
        ))}
      </div>

      {/* Artist Lounge - text only, no image provided */}
      <SubHeading>Artist Lounge (30&apos; x 50&apos;)</SubHeading>
      <Paragraph>
        The Artist Lounge was another key area where performers could unwind and recharge between sets. Our 30&apos; x 50&apos; stretch tent provided a comfortable and inviting space that encouraged relaxation and camaraderie among artists. The tent was outfitted with comfortable seating, refreshments, and ambient lighting, creating a welcoming environment where artists could relax and socialise. The flexible design allowed for the creation of different zones within the lounge, catering to the diverse needs of the performers.
      </Paragraph>

      {/* Impact */}
      <SectionHeading>The Impact of Pop Pro Tents at Lightning in a Bottle</SectionHeading>
      <Paragraph>
        The success of our stretch tents at Lightning in a Bottle was evident in the positive feedback we received from both the festival organisers and attendees. Our tents not only met the functional needs of the festival but also contributed to the overall aesthetic and atmosphere of the event. The versatility of our stretch tents allowed us to create unique and customised spaces that enhanced the festival experience for everyone involved.
      </Paragraph>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
        {[
          { icon: 'ri-palette-line', label: 'Aesthetic Harmony', detail: 'Tents blended with the festival\'s creative ethos' },
          { icon: 'ri-shield-check-line', label: 'Structural Reliability', detail: 'Zero incidents across all 17 tent installations' },
          { icon: 'ri-group-line', label: 'Community Spaces', detail: 'Dedicated zones for every type of festival-goer' },
        ].map((item) => (
          <div key={item.label} className="bg-gray-50 rounded-2xl p-6 text-center">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl mx-auto mb-3">
              <i className={`${item.icon} text-teal-500 text-lg`}></i>
            </div>
            <p className="font-bold text-gray-900 text-sm mb-1">{item.label}</p>
            <p className="text-gray-500 text-xs">{item.detail}</p>
          </div>
        ))}
      </div>

      {/* Environmental */}
      <SectionHeading>Environmental Considerations</SectionHeading>
      <Paragraph>
        At Pop Pro Tents, we understand the importance of sustainability, especially in the context of large-scale events like Lightning in a Bottle. Our stretch tents are designed with environmental considerations in mind, using materials that are both durable and eco-friendly.
      </Paragraph>
      <Paragraph>
        The tents are reusable and easy to transport, reducing the environmental impact associated with traditional event structures. Additionally, the natural colours and organic shapes of our tents blend harmoniously with the surrounding environment, minimising visual disruption and enhancing the overall aesthetic of the festival.
      </Paragraph>

      {/* Conclusion */}
      <SectionHeading>Conclusion</SectionHeading>
      <Paragraph>
        The Lightning in a Bottle Festival was a perfect example of how Pop Pro Tents can transform an event, creating spaces that are not only functional but also beautiful and inspiring. Our stretch tents played a crucial role in shaping the festival experience, providing attendees with comfortable and visually stunning environments where they could relax, connect, and immerse themselves in the festival's offerings.
      </Paragraph>
      <Paragraph>
        We are proud to have been a part of this extraordinary event and look forward to continuing to provide innovative and sustainable solutions for festivals and events around the world. If you're planning an event and looking for versatile, stylish, and eco-friendly tent solutions, Pop Pro Tents is here to help.
      </Paragraph>

      {/* Internal links */}
      <div className="flex flex-wrap gap-3 mt-6">
        <Link
          to="/products/standard-stretch-tents"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-tent-line"></i>
          View Our Tents
        </Link>
        <Link
          to="/blog/stretch-tent-rentals-guide-tips-considerations"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-book-open-line"></i>
          Rental Guide
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-mail-line"></i>
          Inquire Now
        </Link>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
        <span className="text-xs text-gray-400 mr-1 self-center">Tags:</span>
        {['Stretch Tents', 'Case Study', 'Festival', 'Lightning in a Bottle', 'Event Setup', 'Outdoor Events'].map((tag) => (
          <span
            key={tag}
            className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}
